import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ReportStatus } from '@dsr/shared-types';
import { useDars, useCreateDar } from '../api/queries/dars';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { toast } from '../components/Toasts';
import { DateInput } from '../components/DateInput';
import {
  Button,
  Dialog,
  FormField,
  FormRow,
  Select,
  StatusBadge,
  Table,
  Textarea,
  type SelectOption,
} from '../components/ui';
import { fmtDate, fmtTime } from '../utils/date';
import { STATUS_LABEL, firstFieldError } from './IncidentsPage';
import './reports.css';

const STATUSES: ReportStatus[] = [
  'draft',
  'submitted',
  'under_review',
  'approved',
  'rejected',
];

export function DARsPage(): JSX.Element {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<ReportStatus | 'all'>('all');

  const { data, isPending, error } = useDars({
    ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
  });

  const createDar = useCreateDar();
  const [showNew, setShowNew] = useState(false);
  const [reportDate, setReportDate] = useState<number | null>(Date.now());
  const [shiftStart, setShiftStart] = useState<number | null>(null);
  const [shiftEnd, setShiftEnd] = useState<number | null>(null);
  const [summary, setSummary] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const resetForm = (): void => {
    setReportDate(Date.now());
    setShiftStart(null);
    setShiftEnd(null);
    setSummary('');
    setFormError(null);
  };

  const onCreate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setFormError(null);
    try {
      const res = await createDar.mutateAsync({
        report_date: reportDate ?? undefined,
        shift_start: shiftStart ?? undefined,
        shift_end: shiftEnd ?? undefined,
        summary: summary.trim() || undefined,
      });
      toast.success('Daily activity report created.');
      setShowNew(false);
      resetForm();
      navigate(`/dars/${res.dar.id}`);
    } catch (err) {
      setFormError(firstFieldError(err, 'Could not create the report.'));
    }
  };

  if (isPending) return <p className="rpt-status-line">Loading daily activity reports…</p>;
  if (error) {
    const msg =
      error instanceof ApiError && error.status === 403
        ? "You don't have permission to view daily activity reports."
        : 'Failed to load daily activity reports.';
    return <p className="rpt-status-line rpt-status-error">{msg}</p>;
  }

  const dars = data?.dars ?? [];

  const statusFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All statuses' },
    ...STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] })),
  ];

  return (
    <section className="rpt-page">
      <header className="rpt-header">
        <div>
          <h2>Daily activity reports</h2>
          <p className="rpt-sub">Per-shift activity logs and their approval status.</p>
        </div>
        <div className="rpt-header-actions">
          {can('dar.write') && (
            <Button variant="primary" onClick={() => setShowNew(true)}>
              New report
            </Button>
          )}
        </div>
      </header>

      <div className="rpt-card">
        <div className="rpt-filters">
          <label className="rpt-filter">
            <span>Status</span>
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as ReportStatus | 'all')}
              options={statusFilterOptions}
              aria-label="Filter by status"
            />
          </label>
        </div>
      </div>

      <div className="rpt-card rpt-card-tight">
        {dars.length === 0 ? (
          <p className="rpt-empty">No daily activity reports match these filters.</p>
        ) : (
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell>Report date</Table.HeaderCell>
                <Table.HeaderCell>Shift</Table.HeaderCell>
                <Table.HeaderCell>Summary</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {dars.map((dar) => (
                <Table.Row
                  key={dar.id}
                  interactive
                  onClick={() => navigate(`/dars/${dar.id}`)}
                  tabIndex={0}
                  role="link"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') navigate(`/dars/${dar.id}`);
                  }}
                >
                  <Table.Cell tone="strong">{fmtDate(dar.report_date)}</Table.Cell>
                  <Table.Cell>
                    {dar.shift_start || dar.shift_end
                      ? `${fmtTime(dar.shift_start)} – ${fmtTime(dar.shift_end)}`
                      : '—'}
                  </Table.Cell>
                  <Table.Cell>{dar.summary ? truncate(dar.summary, 80) : '—'}</Table.Cell>
                  <Table.Cell>
                    <StatusBadge status={dar.status}>{STATUS_LABEL[dar.status]}</StatusBadge>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </div>

      <Dialog
        title="New daily activity report"
        open={showNew}
        busy={createDar.isPending}
        onClose={() => {
          if (!createDar.isPending) {
            setShowNew(false);
            resetForm();
          }
        }}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setShowNew(false);
                resetForm();
              }}
              disabled={createDar.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="new-dar-form"
              variant="primary"
              disabled={createDar.isPending}
            >
              {createDar.isPending ? 'Creating…' : 'Create report'}
            </Button>
          </>
        }
      >
        <form id="new-dar-form" className="rpt-form" onSubmit={onCreate}>
          <FormField label="Report date">
            <DateInput
              value={reportDate}
              onChange={setReportDate}
              disabled={createDar.isPending}
            />
          </FormField>
          <FormRow>
            <FormField label="Shift start">
              <DateInput
                value={shiftStart}
                onChange={setShiftStart}
                withTime
                disabled={createDar.isPending}
              />
            </FormField>
            <FormField label="Shift end">
              <DateInput
                value={shiftEnd}
                onChange={setShiftEnd}
                withTime
                disabled={createDar.isPending}
              />
            </FormField>
          </FormRow>
          <FormField label="Summary">
            <Textarea
              className="rpt-textarea"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Shift overview, post coverage, notable activity…"
              maxLength={50_000}
              disabled={createDar.isPending}
            />
          </FormField>
          {formError && (
            <p className="rpt-error" role="alert">
              {formError}
            </p>
          )}
        </form>
      </Dialog>
    </section>
  );
}

function truncate(text: string, max: number): string {
  const trimmed = text.trim();
  return trimmed.length > max ? `${trimmed.slice(0, max)}…` : trimmed;
}
